import apiClient from "../../apiClient";
import axios from "axios";
import { API_ENDPOINTS, STORAGE_KEYS } from "../../config";

const quotationService = {

  /**
   * Create a new quotation (with attachments)
   */
  createQuotation: async (formData) => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.CRM.QUOTATIONS,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );

      return {
        success: response.responseCode === 201,
        quotation: response.data
      };

    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.responseMessage || "Failed to create quotation",
      };
    }
  },

  /**
   * Update quotation (with attachments)
   */
  updateQuotation: async (quoteId, formData) => {
    try {
      const response = await apiClient.patch(
        `${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );

      return {
        success: response.responseCode === 200,
        quotation: response.data
      };

    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.responseMessage || "Failed to update quotation",
      };
    }
  },

  /**
   * Finalize quotation
   */
  finalizeQuotation: async (quoteId) => {
    try {
      const response = await apiClient.patch(
        `${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}/finalize`,
        {}
      );

      return {
        success: response.responseCode === 200,
        quotation: response.data
      };

    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.responseMessage || "Failed to finalize quotation"
      };
    }
  },

  /**
   * Send quotation via email
   */
  sendQuotation: async (quoteId, emailData) => {
    try {
      const response = await apiClient.post(
        `${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}/send`,
        emailData
      );

      return {
        success: response.responseCode === 200,
        messageId: response.data.messageId,
        pdfUrl: response.data.pdfUrl
      };

    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.responseMessage || "Failed to send quotation"
      };
    }
  },

  /**
   * Get single quotation
   */
  getQuotationById: async (quoteId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      // Add any additional params (e.g., ordered=true)
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          queryParams.append(key, params[key]);
        }
      });

      const queryString = queryParams.toString();
      const url = queryString
        ? `${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}?${queryString}`
        : `${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}`;

      const response = await apiClient.get(url);

      return {
        success: response.responseCode === 200,
        quotation: response.data
      };

    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.responseMessage || "Failed to retrieve quotation"
      };
    }
  },

  /**
   * Get quotations under an opportunity
   */
  getQuotationsByOpportunity: async (opportunityId, filters = {}) => {
    try {
      const params = new URLSearchParams();
      params.append("opportunityId", opportunityId);
      if (filters.status) params.append("status", filters.status);

      const response = await apiClient.get(
        `${API_ENDPOINTS.CRM.QUOTATIONS}?${params.toString()}`
      );

      return {
        success: response.responseCode === 200,
        quotations: Array.isArray(response.data) ? response.data : []
      };

    } catch (error) {
      return {
        success: false,
        quotations: [],
        error: error.response?.data?.responseMessage || "Failed to retrieve quotations"
      };
    }
  },

  /**
   * Get quotations by customer ID with pagination
   */
  getQuotationsByCustomer: async (customerId, params = {}) => {
    try {
      const { page = 1, limit = 10 } = params;
      const queryParams = new URLSearchParams();
      queryParams.append('page', page);
      queryParams.append('limit', limit);

      const response = await apiClient.get(
        `${API_ENDPOINTS.CRM.QUOTATIONS}/by-customer/${customerId}?${queryParams.toString()}`
      );

      return {
        success: response.responseCode === 200,
        data: response.data?.quotations || [],
        pagination: response.data?.pagination || {}
      };

    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.responseMessage || "Failed to retrieve quotations",
        data: [],
        pagination: {}
      };
    }
  },

  /**
   * Delete quotation
   */
  deleteQuotation: async (quoteId) => {
    try {
      const response = await apiClient.delete(
        `${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}`
      );

      return {
        success: response.responseCode === 200,
        message: response.responseMessage || "Quotation deleted successfully"
      };

    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.responseMessage || "Failed to delete quotation"
      };
    }
  },

  /**
   * Download invoice PDF
   */
  downloadInvoice: async (quoteId) => {
    try {
      // Use axios directly to access full response with headers
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);

      const response = await axios.get(
        `${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}/download-invoice`,
        {
          responseType: 'blob',
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            ...apiClient.defaults.headers
          }
        }
      );

      // Generate filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `Invoice_${quoteId}_${new Date().toISOString().split('T')[0]}.pdf`;

      if (contentDisposition) {
        // Use a more robust regex to extract filename without trailing quotes
        const fileNameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/i);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = decodeURIComponent(fileNameMatch[1]);
        }
      }

      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return {
        success: true,
        message: 'Invoice downloaded successfully'
      };

    } catch (error) {

      // Try to parse error message from blob response
      if (error.response && error.response.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorData = JSON.parse(errorText);
          return {
            success: false,
            error: errorData.responseMessage || errorData.error || "Failed to download invoice"
          };
        } catch (parseError) {
          // If parsing fails, use default error message
        }
      }

      return {
        success: false,
        error: error.response?.data?.responseMessage || error.message || "Failed to download invoice"
      };
    }
  },

  /**
   * Delete attachment from quotation
   */
  deleteAttachment: async (quoteId, attachmentId) => {
    try {
      const response = await apiClient.delete(
        `${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}/attachments/${attachmentId}`
      );

      return {
        success: response.responseCode === 200,
        message: response.responseMessage || "Attachment deleted successfully"
      };

    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.responseMessage || "Failed to delete attachment"
      };
    }
  }
};

export default quotationService;
